import { addDays, format, parseISO } from "date-fns";
import type { LaseredCondition } from "@/types";
import { DATE_FORMATS, formatDate, todayUtcYMD } from "@/utils/formatters/dateFormatter";
import { formatUSD } from "@/utils/formatters/currencyFormatter";

/**
 * Expected triggers on conditional lasers (#7361, PO 2026-10-07): the TPA reports
 * a qualifying claim still in adjudication, so the condition counts as triggered
 * on the expected date of service until the real line arrives (the line's date
 * then wins, with no review). Mirrors the mgmt-api validation of
 * `PUT .../expected-trigger`.
 */

/** Days after the expected date of service for the default review-by date. */
export const EXPECTED_TRIGGER_REVIEW_DAYS = 60;

const ymd = (d?: string) => (d ? d.slice(0, 10) : "");
const day = (d?: string) => (d ? formatDate(ymd(d)) : "—");
const isYMD = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v);

/** Expected date + 60 days, no later than the paid window's end; "" without a valid date. */
export const defaultReviewBy = (expectedDate: string, paidEnd?: string): string => {
  if (!isYMD(expectedDate)) return "";
  const plus = format(addDays(parseISO(expectedDate), EXPECTED_TRIGGER_REVIEW_DAYS), DATE_FORMATS.INPUT);
  const cap = ymd(paidEnd);
  return cap && plus > cap ? cap : plus;
};

export interface ExpectedTriggerInput {
  expected_date: string;
  reason: string;
  review_by: string;
}

export type ExpectedTriggerErrors = Partial<Record<keyof ExpectedTriggerInput, string>>;

type ConditionWindowsOf = Pick<LaseredCondition, "incurred_start_date" | "incurred_end_date" | "paid_end_date">;

/**
 * Review-by rules shared by the set dialog and the overdue review's "keep":
 * on or after the expected date of service, on or before the paid window's end
 * (a claim paid after it cannot trigger the condition). "" when valid.
 */
export const reviewByError = (reviewBy: string, expectedDate: string | undefined, c?: ConditionWindowsOf): string => {
  if (!reviewBy) return "Enter a review-by date.";
  if (!isYMD(reviewBy)) return "Enter a valid date.";
  if (expectedDate && reviewBy < ymd(expectedDate)) return "Must be on or after the expected date of service.";
  const paidEnd = ymd(c?.paid_end_date);
  if (paidEnd && reviewBy > paidEnd) return `Must be on or before the paid window's end (${day(paidEnd)}).`;
  return "";
};

/**
 * A review-by date already behind the server's today (UTC): the resolver treats
 * the expected trigger as overdue, and keeping it is refused (!4881 thread 30545).
 */
export const reviewByHasPassed = (reviewBy: string, today: string = todayUtcYMD()): boolean =>
  !!reviewBy && reviewBy < today;

/** Blocking errors per field; empty when the expected trigger can be saved. */
export const validateExpectedTrigger = (v: ExpectedTriggerInput, c: ConditionWindowsOf): ExpectedTriggerErrors => {
  const errors: ExpectedTriggerErrors = {};
  const start = ymd(c.incurred_start_date);
  const end = ymd(c.incurred_end_date);
  const paidEnd = ymd(c.paid_end_date);
  if (!v.expected_date) errors.expected_date = "Enter the expected date of service.";
  else if (!isYMD(v.expected_date)) errors.expected_date = "Enter a valid date.";
  else if ((start && v.expected_date < start) || (end && v.expected_date > end))
    errors.expected_date = `Must be inside the incurred window (${day(start)} – ${day(end)}).`;
  // As the API: a date of service after the paid window's end can't trigger the
  // condition. Reported here, not on Review By, which can't fix it (!4881 thread 30688).
  else if (paidEnd && v.expected_date > paidEnd)
    errors.expected_date = `Must be on or before the paid window's end (${day(paidEnd)}).`;
  if (!v.reason.trim()) errors.reason = "Enter a reason.";
  const rb = reviewByError(v.review_by, errors.expected_date ? undefined : v.expected_date, c);
  if (rb) errors.review_by = rb;
  return errors;
};

/** An expected trigger that still applies (not arrived, not cancelled). */
export const hasLiveExpectedTrigger = (c?: Pick<LaseredCondition, "expected_trigger">): boolean =>
  c?.expected_trigger?.state === "active" || c?.expected_trigger?.state === "overdue";

/** A condition's windows as stored or as typed in the editor (YYYY-MM-DD; a blank end is open). */
export interface ExpectedTriggerTermsWindows {
  incurred_start_date?: string;
  incurred_end_date?: string;
  paid_end_date?: string;
}

/**
 * Why a condition's windows do not cover its live expected trigger, if they do
 * not (#7361; the API accepts such an edit):
 * - `outside_incurred`: the expected date of service is outside the incurred
 *   window, so the resolver ignores the expected trigger (its record still
 *   reads active or overdue).
 * - `review_by_after_paid_end`: the review-by date is after the paid window's
 *   end; the expected trigger still applies, but a claim paid after the window
 *   cannot trigger the condition.
 * Undefined when they do, or there is no live expected trigger.
 */
export type ExpectedTriggerGap = "outside_incurred" | "review_by_after_paid_end";

/** The coverage rule: the gap windows `w` leave in the live expected trigger `et`, if any. */
export const expectedTriggerGap = (
  et: LaseredCondition["expected_trigger"],
  w: ExpectedTriggerTermsWindows
): ExpectedTriggerGap | undefined => {
  if (!et || !hasLiveExpectedTrigger({ expected_trigger: et })) return undefined;
  const date = ymd(et.expected_date);
  const start = ymd(w.incurred_start_date);
  const end = ymd(w.incurred_end_date);
  if ((start && date < start) || (end && date > end)) return "outside_incurred";
  const paidEnd = ymd(w.paid_end_date);
  if (paidEnd && ymd(et.review_by) > paidEnd) return "review_by_after_paid_end";
  return undefined;
};

/**
 * A live expected trigger the resolver ignores: its expected date of service is
 * outside the condition's current incurred window (edited after it was set).
 * Shown as not applied, never as in force.
 */
export const expectedTriggerNotApplied = (
  c?: Pick<LaseredCondition, "expected_trigger"> & ExpectedTriggerTermsWindows
): boolean => !!c && expectedTriggerGap(c.expected_trigger, c) === "outside_incurred";

export const EXPECTED_TRIGGER_NOT_APPLIED = "Not applied: outside the incurred window";

type SavedForWarning = Pick<LaseredCondition, "expected_trigger"> &
  Partial<Pick<LaseredCondition, "amt_lasered_conditional_deductible">> &
  ExpectedTriggerTermsWindows;

/**
 * The edit brings a live expected trigger that was outside the incurred window
 * ("Not applied") back inside it, so it applies again (PO 2026-10-14: warn, as
 * when an edit strands one).
 */
export const expectedTriggerReapplies = (
  saved: SavedForWarning | undefined,
  edited: ExpectedTriggerTermsWindows
): boolean => {
  const et = saved?.expected_trigger;
  return (
    !!et &&
    expectedTriggerGap(et, saved as SavedForWarning) === "outside_incurred" &&
    expectedTriggerGap(et, edited) !== "outside_incurred"
  );
};

/**
 * Warning for an edit of a saved condition that changes whether its live
 * expected trigger applies (PO 2026-10-07: warn, then save anyway or go back;
 * PO 2026-10-14: also when the edit brings it back):
 * - the new windows no longer cover it (only a gap the edit introduces);
 * - the new windows cover it again after it was "Not applied".
 * "" otherwise, and when there is no live expected trigger. The same sentence
 * shows on the row while editing and in the save confirm.
 */
export const strandedExpectedTriggerWarning = (
  saved: SavedForWarning | undefined,
  edited: ExpectedTriggerTermsWindows & { amt?: string }
): string => {
  const et = saved?.expected_trigger;
  if (et && expectedTriggerReapplies(saved, edited)) {
    const amount = edited.amt ? Number(edited.amt) : saved?.amt_lasered_conditional_deductible;
    const money =
      amount !== undefined && Number.isFinite(amount) && amount > 0
        ? `${formatUSD(amount, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} `
        : "";
    const overdue = reviewByHasPassed(ymd(et.review_by))
      ? ` Its review-by date (${day(et.review_by)}) has passed, so a deductible review opens and holds the stop-loss claims.`
      : "";
    const paid =
      expectedTriggerGap(et, edited) === "review_by_after_paid_end"
        ? ` Its review-by date (${day(et.review_by)}) is after the paid window's end (${day(edited.paid_end_date)}): a claim paid after the window cannot trigger this conditional laser.`
        : "";
    return `With these terms, the expected trigger from ${day(et.expected_date)} is inside the incurred window again and applies: this conditional laser's ${money}deductible counts from ${day(et.expected_date)}.${overdue}${paid}`;
  }
  const gap = expectedTriggerGap(et, edited);
  if (!et || !gap || gap === expectedTriggerGap(et, saved)) return "";
  if (gap === "outside_incurred")
    return `With these terms, the expected trigger from ${day(et.expected_date)} is outside the incurred window (${day(
      edited.incurred_start_date
    )} – ${day(edited.incurred_end_date)}) and will no longer apply: the conditional laser goes back to waiting for a qualifying claim line.`;
  return `With these terms, the expected trigger's review-by date (${day(et.review_by)}) is after the paid window's end (${day(
    edited.paid_end_date
  )}). It still applies, but a claim paid after the window cannot trigger this conditional laser.`;
};

/**
 * "Set expected trigger" is offered on a saved condition that has not triggered
 * (armed) on a claimant without a contract laser or exclusion (#7361 AC7: there,
 * conditional lasers are inert, so it would change nothing). Not while a live
 * expected trigger is not applied (outside the incurred window): its Edit replaces it.
 */
export const canSetExpectedTrigger = (c: LaseredCondition | undefined, contractClaimant: boolean): boolean =>
  !!c && !contractClaimant && c.lasered_condition_trigger_state === "armed" && !hasLiveExpectedTrigger(c);

/**
 * What the state chip and the counts show. A trigger sourced from an active
 * expected trigger reads "Expected", an overdue one "Overdue"; otherwise the
 * server's trigger state.
 */
export type ConditionDisplayState = "armed" | "triggered" | "expected" | "overdue" | "inert";

export const conditionDisplayState = (
  c: Pick<LaseredCondition, "lasered_condition_trigger_state" | "expected_trigger" | "triggered_by_claim_item_id"> &
    ExpectedTriggerTermsWindows
): ConditionDisplayState => {
  const s = c.lasered_condition_trigger_state;
  if (s === "inert") return "inert";
  // Outside the incurred window the resolver ignores it: the server's state stands.
  if (expectedTriggerNotApplied(c)) return s;
  if (c.expected_trigger?.state === "overdue") return "overdue";
  if (s === "triggered" && c.expected_trigger?.state === "active" && !c.triggered_by_claim_item_id) return "expected";
  return s;
};

const STATE_ORDER: ConditionDisplayState[] = ["armed", "triggered", "expected", "overdue", "inert"];

/** "2 armed, 1 expected" (#3512 AC2, #7361); "" with no conditions. */
export const conditionStateCountsText = (
  conditions: Parameters<typeof conditionDisplayState>[0][] | undefined
): string => {
  const states = (conditions ?? []).map(conditionDisplayState);
  return STATE_ORDER.map((s) => [states.filter((x) => x === s).length, s] as const)
    .filter(([n]) => n > 0)
    .map(([n, s]) => `${n} ${s}`)
    .join(", ");
};
